import { CompositeFilterDescriptor, FilterDescriptor, FilterOperator } from '@progress/kendo-data-query';
import { getGearDateFilterFormat, getGearDateTimeFilterFormat } from '@cs-cms/utils';
import { CpsFilterRequest, CPS_FIELD_TYPE } from '@cs-cms/types';
import { GlobalFilterState } from './GlobalFilterState';

export const PICKER_START_ID = 'dateTimePickerStart';
export const PICKER_END_ID = 'dateTimePickerEnd';

export type GroupedDatePickerId = typeof PICKER_START_ID | typeof PICKER_END_ID;

export function getInitialFieldSelected(
  dateFiltersState: GlobalFilterState[]
): GlobalFilterState | undefined {
  if (dateFiltersState.length === 0) {
    return undefined;
  }
  return dateFiltersState[0];
}

export function getDateFilterFieldNames(dateFiltersState: GlobalFilterState[]): string[] {
  return dateFiltersState.map(state => state.name);
}

export function removeDateFiltersFromRequest(
  filters: CpsFilterRequest[] | undefined,
  dateFieldNames: string[]
): CpsFilterRequest[] {
  return filters?.filter(filter => !dateFieldNames.includes(filter.fieldName)) ?? [];
}

export function shouldRetainFilterOnPickerChange(
  filter: CpsFilterRequest,
  fieldName: string,
  pickerId: GroupedDatePickerId
): boolean {
  if (filter.fieldName !== fieldName) {
    return true;
  }
  if (pickerId === PICKER_START_ID) {
    return filter.operator === FilterOperator.LessThanOrEqual;
  }
  return filter.operator === FilterOperator.GreaterThanOrEqual;
}

export function removeFieldFromCompositeFilter(
  descriptor: CompositeFilterDescriptor,
  fieldName: string
): CompositeFilterDescriptor {
  return {
    ...descriptor,
    filters: (descriptor.filters as CompositeFilterDescriptor[]).filter(filterGroup =>
      (filterGroup.filters as FilterDescriptor[]).some(filter => filter.field !== fieldName)
    )
  };
}

export function formatGroupedDateFilterValue(field: GlobalFilterState, value: Date): string {
  if (field.type === CPS_FIELD_TYPE.DATETIME) {
    return getGearDateTimeFilterFormat(value);
  }
  return getGearDateFilterFormat(value);
}

export function buildGroupedDateRangeFilter(
  field: GlobalFilterState,
  pickerId: GroupedDatePickerId,
  value: Date
): CpsFilterRequest | undefined {
  const formattedValue = formatGroupedDateFilterValue(field, value);

  if (pickerId === PICKER_START_ID) {
    return {
      fieldName: field.name,
      operator: FilterOperator.GreaterThanOrEqual,
      values: [formattedValue],
      fieldType: CPS_FIELD_TYPE.DATE,
      isGlobal: true
    } as CpsFilterRequest;
  }

  if (pickerId === PICKER_END_ID) {
    return {
      fieldName: field.name,
      operator: FilterOperator.LessThanOrEqual,
      values: [formattedValue],
      fieldType: CPS_FIELD_TYPE.DATE,
      isGlobal: true
    } as CpsFilterRequest;
  }

  return undefined;
}

export function toGroupedDatePickerValue(value: Date | Date[] | null): Date | null {
  if (!value) {
    return null;
  }
  if (Array.isArray(value)) {
    if (value.length === 0) {
      return null;
    }
    return value[0];
  }
  return value;
}

export function isGroupedDatePickerId(id: string | undefined): id is GroupedDatePickerId {
  return id === PICKER_START_ID || id === PICKER_END_ID;
}

export function getGroupedDateRangeValues(
  filters: CpsFilterRequest[] | undefined,
  fieldName: string
): { start?: string[]; end?: string[] } {
  const fieldFilters = filters?.filter(
    filter => filter.fieldName === fieldName && filter.isGlobal
  );

  return {
    start: fieldFilters?.find(filter => filter.operator === FilterOperator.GreaterThanOrEqual)
      ?.values as string[] | undefined,
    end: fieldFilters?.find(filter => filter.operator === FilterOperator.LessThanOrEqual)?.values as
      | string[]
      | undefined
  };
}

export function getFieldDisplayName(
  dateFiltersState: GlobalFilterState[],
  fieldSelected: GlobalFilterState
): string {
  const translatedField = dateFiltersState.find(filter => filter.id === fieldSelected.id);
  return translatedField?.displayName ?? fieldSelected.displayName ?? '';
}

export function getEndOfToday(): Date {
  return new Date(new Date().setHours(23, 59, 59, 999));
}

export function parseFilterDateValue(values: string[] | undefined): Date | null {
  if (!values || values.length === 0) {
    return null;
  }
  return new Date(values[0]);
}
